import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { Link, useLoaderData } from '@remix-run/react';
import { useTranslation } from 'react-i18next';

import PostCardv2 from '~/components/PostCardv2';
import PostCardv3 from '~/components/PostCardv3';
import Typography from '~/components/Typography';

import type { Card } from '~/interfaces/home_page';
import type { Post } from '~/interfaces/post';

import usePostsInsights from '~/hooks/useLoaders/usePostsInsights';

import SectionHeading from '../SectionHeading';
import Skeleton from './Skeleton';

interface Props {
  posts: Post[];
  card: Card;
  title: string;
  description?: string;
  isLoading: boolean;
  ctaPath?: string;
  fontFamily: string;
  isNewWebBuilder?: boolean;
}

const FeaturedSection = ({
  isLoading,
  posts,
  card,
  title,
  description,
  ctaPath = '',
  fontFamily,
  isNewWebBuilder = false,
}: Props) => {
  const { t } = useTranslation();
  const { homePage, cardStyles } = useLoaderData<any>();
  const accentColor = homePage?.general_section?.theme?.accent_color;
  const textColor = homePage?.general_section?.theme?.text_color;

  const { postsWithInsights } = usePostsInsights({
    incomingPosts: posts,
  });

  if (!posts || posts.length === 0) {
    return null;
  }

  return (
    <div className="space-y-6 py-8">
      <div className="flex items-center justify-between">
        <SectionHeading
          title={title}
          description={description}
          fontFamily={fontFamily}
          accentColor={accentColor}
          textColor={textColor}
          isNewWebBuilder={isNewWebBuilder}
        />
        {ctaPath && (
          <Link prefetch="intent" to={ctaPath} className="flex items-center space-x-0.5 opacity-70 hover:opacity-100">
            <Typography
              as="span"
              size="md"
              fontFamily={fontFamily}
              fontFamilyv2={isNewWebBuilder ? fontFamily : undefined}
              color={accentColor}
              className="flex items-center"
            >
              {t('shared.cta.view_more')}
              <ChevronRightIcon className="ml-2 mt-0.5 h-4 w-4" />
            </Typography>
          </Link>
        )}
      </div>
      {isLoading ? (
        <Skeleton />
      ) : (
        <div className="grid grid-cols-3 gap-6">
          {postsWithInsights?.map((post) => {
            return (
              <div className="col-span-3 md:col-span-1" key={post.id}>
                {isNewWebBuilder ? (
                  <PostCardv3 key={post.id} post={post} cardStyles={cardStyles} />
                ) : (
                  <PostCardv2 key={post.id} post={post} card={card} />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FeaturedSection;
